// This file is kept for backward compatibility
// All API calls should now use src/services/api.ts

import { User, ExchangeAccount, Rebate, Withdrawal, Transaction, CheckIn, Reward, RedeemedReward, DailyReward, Task, PointsHistory } from '../types';
import * as api from './api';

// Re-export API functions as mockApi for backward compatibility
export const mockApi = {
  login: async (telegramUser: any): Promise<User> => {
    const response = await api.authApi.loginWithTelegram({
      telegramId: telegramUser.id,
      firstName: telegramUser.first_name || '',
      lastName: telegramUser.last_name || '',
      username: telegramUser.username || '',
    });
    
    if (response.success && response.user) {
      return {
        id: response.user.id,
        telegramId: response.user.telegramId,
        username: response.user.username || '',
        firstName: response.user.firstName || '',
        lastName: response.user.lastName || '',
        balance: response.user.balance || 0,
        totalEarned: 0,
        pendingRebate: response.user.pendingRebate || 0,
        points: response.user.points || 0,
        createdAt: response.user.createdAt,
      };
    }
    throw new Error('Login failed');
  },
  
  getExchangeAccounts: async (_userId: string): Promise<ExchangeAccount[]> => {
    return api.exchangeApi.getAccounts();
  },
  
  linkExchangeAccount: async (_userId: string, exchange: string, uid: string): Promise<ExchangeAccount> => {
    return api.exchangeApi.linkAccount({
      exchange: exchange as any,
      uid,
    });
  },
  
  deleteExchangeAccount: async (accountId: string): Promise<boolean> => {
    await api.exchangeApi.deleteAccount(accountId);
    return true;
  },
  
  getRebates: async (_userId: string): Promise<Rebate[]> => {
    return api.rebateApi.getRebates();
  },
  
  getWithdrawals: async (_userId: string): Promise<Withdrawal[]> => {
    return api.withdrawalApi.getWithdrawals();
  },
  
  withdraw: async (_userId: string, amount: number, walletAddress: string, network: string): Promise<Withdrawal> => {
    return api.withdrawalApi.createWithdrawal({
      amount,
      networkId: network,
      walletAddress,
    });
  },
  
  getTransactions: async (_userId: string): Promise<Transaction[]> => {
    return api.transactionApi.getTransactions();
  },
  
  checkIn: async (userId: string): Promise<CheckIn & { newPoints?: number }> => {
    const response = await api.checkInApi.checkIn();
    return {
      id: response.checkIn.id,
      userId,
      date: response.checkIn.date,
      points: response.checkIn.points,
      day: response.checkIn.day,
      createdAt: response.checkIn.createdAt || new Date().toISOString(),
      newPoints: response.newPoints,
    };
  },
  
  getCheckIns: async (_userId: string): Promise<CheckIn[]> => {
    const response = await api.checkInApi.getCheckIns();
    return response.checkIns || [];
  },
  
  getDailyRewards: async (): Promise<DailyReward[]> => {
    const response = await api.checkInApi.getDailyRewards();
    return response.dailyRewards || [];
  },
  
  getTasks: async (category?: 'special' | 'social' | 'trading'): Promise<Task[]> => {
    const response = await api.taskApi.getTasks(category);
    return response.tasks || [];
  },
  
  claimTask: async (_userId: string, taskId: string): Promise<{ success: boolean; newPoints?: number }> => {
    const response = await api.taskApi.claimTask(taskId);
    return {
      success: true,
      newPoints: response.newPoints,
    };
  },
  
  getRewards: async (): Promise<Reward[]> => {
    const response = await api.rewardApi.getRewards();
    return response.rewards || [];
  },
  
  redeemReward: async (_userId: string, rewardId: string): Promise<RedeemedReward> => {
    const response = await api.rewardApi.redeemReward(rewardId);
    return {
      id: response.redeemedReward.id,
      userId: _userId,
      rewardId,
      rewardName: response.redeemedReward.rewardName || '',
      pointsUsed: response.redeemedReward.pointsSpent,
      status: response.redeemedReward.status,
      code: response.redeemedReward.code,
      createdAt: response.redeemedReward.redeemedAt || new Date().toISOString(),
    };
  },
  
  getRedeemedRewards: async (_userId: string): Promise<RedeemedReward[]> => {
    return api.rewardApi.getRedeemedRewards();
  },
  
  getPointsHistory: async (_userId: string): Promise<PointsHistory[]> => {
    const response = await api.pointsHistoryApi.getPointsHistory();
    return response.pointsHistory || [];
  },
};
